import {
  Button,
  DatePickerWithInput,
  ModalLayout,
  Pagination,
  SearchFilters,
} from "@wac-ui-dashboard/wac_component_library";
import React from "react";
import { FaSort } from "react-icons/fa";
import { Table } from "../../../Global/Table";
import OrderColumn from "../../../Global/OrderColumn";
import CashReceiptForm from "../CashReceiptForm";
import SupplierPaymentForm from "../SupplierPaymentForm";
import ReceiptPdf from "../Print/ReceiptPdf";
import useCompleted from "./useCompleted";

const Completed = () => {
  const {
    paginationOptions,
    queryState,
    reduxState,
    currentData,
    isFetching,
    isLoading,
    isReprinting,
    isFetchingEdit,
    isCancelling,
    isExporting,
    showEditModal,
    editPermission,
    cancelPermission,
    tableFields,
    tableData,
    receiptRef,
    date,
    cancellationReason,
    setCancellationReason,
    refetch,
    getRow,
    handleDateChange,
    handleSearch,
    handleSort,
    handlePageSize,
    handlePagination,
    handleReprint,
    handleEdit,
    handleCloseEditModal,
    handleCancelClick,
    handleCloseCancelModal,
    handleConfirmCancel,
    handleExport,
    handleEditColumnsClick,
    updateCashReceiptFields,
    triggerReceiptPrint,
  } = useCompleted();

  return (
    <>
      <div className="col-auto pro-pt-3">
        <SearchFilters
          onSearchInput={handleSearch}
          SearchIcon={
            <span className="material-symbols-outlined"> search </span>
          }
          showClearFilters={false}
          searchInputProps={{
            value: queryState?.search,
            placeholder: "Search this table",
          }}
          showActions={true}
          handleActionClick={handleEditColumnsClick}
          loading={isLoading}
          filterChildren={
            <div className="col-auto">
              <div className="pro-d-flex pro-items-center pro-gap-2">
                <DatePickerWithInput
                  CalendarOutlineIcon={
                    <span className="material-symbols-outlined">
                      calendar_month
                    </span>
                  }
                  range={true}
                  initialValue={date}
                  onDateChange={handleDateChange}
                />
                <button
                  className={`pro-btn pro-items-center pro-btn-outline ${
                    isExporting ? "loading" : ""
                  }`}
                  onClick={handleExport}
                  disabled={isExporting}
                >
                  <span className="material-symbols-outlined">download</span>
                  <span>Export</span>
                </button>
              </div>
            </div>
          }
        />
      </div>

      <Table
        multiSelect={false}
        data={tableData}
        uniqueID={"_id"}
        fields={tableFields}
        SortIcon={<FaSort />}
        handleSort={handleSort}
        getRow={getRow}
        loading={isFetching}
        perpage={queryState?.currentPageSize}
        assignable={true}
        assignText={isReprinting ? "Printing..." : "Print"}
        assignIcon={<span className="material-symbols-outlined">print</span>}
        handleAssign={handleReprint}
        editable={editPermission}
        editText={isFetchingEdit ? "Loading..." : "Edit"}
        editIcon={<span className="material-symbols-outlined">edit</span>}
        handleEdit={handleEdit}
        deletable={cancelPermission}
        deleteText={"Cancel"}
        deleteIcon={<span className="material-symbols-outlined">cancel</span>}
        handleDelete={handleCancelClick}
        showCheckBox={true}
        clear={queryState?.clear}
      />
      {currentData?.data?.data?.length > 0 && (
        <Pagination
          currentPage={queryState?.currentPage}
          totalPageCount={Math.ceil(
            currentData?.data?.total_count / queryState?.currentPageSize,
          )}
          onPageChange={handlePagination}
          options={paginationOptions}
          onActionChange={handlePageSize}
          center
        />
      )}

      <ModalLayout show={showEditModal} handleClose={handleEditColumnsClick}>
        <div className="pro-m-5">
          <OrderColumn
            title={"Choose which columns you see"}
            refetchData={refetch}
            tableFields={currentData?.data?.fields}
            moduleId={currentData?.data?.module_id}
            updateData={updateCashReceiptFields}
          />
        </div>
      </ModalLayout>

      {/* Edit — invoice cash receipt */}
      <ModalLayout
        show={reduxState?.showEditReceiptModal}
        handleClose={handleCloseEditModal}
        backdrop="static"
      >
        {reduxState?.showEditReceiptModal && (
          <CashReceiptForm
            mode="edit"
            editData={reduxState?.editData}
            onClose={handleCloseEditModal}
            onPrintReady={triggerReceiptPrint}
          />
        )}
      </ModalLayout>

      {/* Edit — supplier payment */}
      <ModalLayout
        show={reduxState?.showEditSupplierModal}
        handleClose={handleCloseEditModal}
        backdrop="static"
      >
        {reduxState?.showEditSupplierModal && (
          <SupplierPaymentForm
            mode="edit"
            editData={reduxState?.editData}
            onClose={handleCloseEditModal}
            onPrintReady={triggerReceiptPrint}
          />
        )}
      </ModalLayout>

      {/* Cancel cash receipt */}
      <ModalLayout
        show={reduxState?.showCancelModal}
        handleClose={handleCloseCancelModal}
        backdrop="static"
      >
        <div className="pro-m-5">
          <h5 className="pro-ttl pro-mb-4">Cancel Cash Receipt</h5>
          <p className="pro-mb-4">
            Are you sure you want to cancel this cash receipt? This action
            cannot be undone.
          </p>
          <div className="input-wrap pro-mb-4">
            <label
              htmlFor="cancellation_reason"
              className="pro-font-sm pro-mb-1 pro-fw-medium"
            >
              Cancellation Reason
            </label>
            <textarea
              id="cancellation_reason"
              name="cancellation_reason"
              rows={3}
              className="pro-input lg"
              value={cancellationReason}
              onChange={(e) => setCancellationReason(e.target.value)}
            />
          </div>
          <div className="col-12 pro-d-flex pro-justify-end pro-mt-4">
            <Button
              className={"pro-btn-link lg pro-px-5"}
              onClick={handleCloseCancelModal}
              disabled={isCancelling}
            >
              Close
            </Button>
            <Button
              className={"pro-btn-primary lg pro-ms-3"}
              onClick={handleConfirmCancel}
              disabled={isCancelling}
            >
              {isCancelling ? "Cancelling..." : "Cancel Receipt"}
            </Button>
          </div>
        </div>
      </ModalLayout>

      <div
        style={{
          position: "fixed",
          opacity: "0",
          visibility: "hidden",
          height: "0",
          width: "0",
        }}
      >
        <ReceiptPdf receiptRef={receiptRef} />
      </div>
    </>
  );
};

export default Completed;
